import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { mockData } from '../data/mockData';
import { TrendingUp, TrendingDown } from 'lucide-react';
import ExecutiveKPITicker from './ExecutiveKPITicker';

const statusColors = {
  strong: { bg: 'bg-emerald-500', text: 'text-emerald-600', light: 'bg-emerald-50', border: 'border-emerald-500' },
  inline: { bg: 'bg-blue-500', text: 'text-blue-600', light: 'bg-blue-50', border: 'border-blue-500' },
  attention: { bg: 'bg-amber-500', text: 'text-amber-600', light: 'bg-amber-50', border: 'border-amber-500' },
  pressure: { bg: 'bg-red-500', text: 'text-red-600', light: 'bg-red-50', border: 'border-red-500' },
};

// Action Block with image - compact height to fit on one screen
const ActionBlock = ({ title, status, phrase, miniKpis, backgroundImage, onClick, delay = 0, navigate, year, inDevelopment = false, isPilot = false }) => {
  const { language } = useLanguage();
  const colors = statusColors[status] || statusColors.inline;

  // Status labels in Portuguese
  const statusLabels = {
    strong: { en: 'Strong', pt: 'Forte' },
    inline: { en: 'On Track', pt: 'No Plano' },
    attention: { en: 'Attention', pt: 'Atenção' },
    pressure: { en: 'Pressure', pt: 'Pressão' },
    pilot: { en: 'Pilot Project', pt: 'Projeto Piloto' },
  };

  // Handle KPI click - navigate to corresponding page
  const handleKpiClick = (e, kpi) => {
    e.stopPropagation(); // Prevent triggering the main card onClick
    if (kpi.link) {
      navigate(kpi.link);
    }
  };

  // Format variance label based on year and type
  const formatVarianceLabel = (variance, type) => {
    if (!variance) return null;
    const budgetLabel = year === 2026 
      ? (language === 'pt' ? 'vs Orç. 1T26' : 'vs Bdg Q126') 
      : (language === 'pt' ? 'vs Orç. 2025' : 'vs Bdg 2025');
    const yoyLabel = year === 2026 
      ? (language === 'pt' ? 'vs 1T25' : 'vs Q125') 
      : (language === 'pt' ? 'vs 2024' : 'vs 2024');
    
    return type === 'budget' ? budgetLabel : yoyLabel;
  };

  // In Development state
  if (inDevelopment) {
    // Check if this is the HR tile to use custom positioning and zoom
    const isHRTile = typeof title === 'object' && title.en === 'Human Resources';
    
    return (
      <div 
        className="group relative overflow-hidden rounded-xl h-[380px] cursor-default transition-all duration-500 animate-fade-in-up opacity-0"
        style={{ animationDelay: `${delay}ms`, animationFillMode: 'forwards' }}
        data-testid={`action-block-${typeof title === 'object' ? title.en : title}`}
      >
        <div 
          className="absolute inset-0"
          style={{ 
            backgroundImage: `url(${backgroundImage})`, 
            filter: isHRTile ? 'grayscale(30%)' : 'grayscale(30%) blur(1px)',
            backgroundPosition: isHRTile ? '40% 40%' : 'center center',
            backgroundSize: isHRTile ? 'cover' : 'cover',
            backgroundRepeat: 'no-repeat',
            transform: isHRTile ? 'scaleX(-1)' : 'none'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/60 to-primary/30" />
        
        <div className="absolute inset-0 p-4 flex flex-col text-white">
          {/* Status badge - "Previsto" */}
          <div className="self-start px-2.5 py-1 rounded-full bg-slate-100 border border-slate-300">
            <span className="text-[9px] font-medium uppercase tracking-widest text-slate-600">
              {language === 'pt' ? 'Previsto' : 'Planned'}
            </span>
          </div>

          {/* Title section */}
          <div className="mt-3">
            <h3 className="font-serif text-[24px] font-semibold tracking-tight">
              {typeof title === 'object' ? title[language] : title}
            </h3>
          </div>

          {/* Single Development Message Box */}
          <div className="mt-10 flex-1 flex items-start">
            <div className="bg-slate-900/80 rounded-lg px-5 py-4 w-full">
              <div className="text-[11px] text-white/60 uppercase tracking-wider font-semibold mb-2">
                {language === 'pt' ? 'Módulo Previsto' : 'Planned Module'}
              </div>
              <p className="text-[13px] text-white/80 leading-relaxed">
                {language === 'pt' 
                  ? 'Disponibilização sujeita à aprovação do cliente para desenvolvimento.'
                  : 'Availability subject to client approval for development.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="group relative overflow-hidden rounded-xl h-[380px] cursor-pointer transition-all duration-500 hover:scale-[1.02] animate-fade-in-up opacity-0"
      style={{ animationDelay: `${delay}ms`, animationFillMode: 'forwards' }}
      onClick={onClick}
      data-testid={`action-block-${typeof title === 'object' ? title.en : title}`}
    >
      <div 
        className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
        style={{ backgroundImage: `url(${backgroundImage})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/50 to-transparent" />
      
      <div className="absolute inset-0 p-4 flex flex-col text-white">
        {/* Status badge - fixed at top - sans-serif clean */}
        {isPilot ? (
          <div className="self-start px-2.5 py-1 rounded-full bg-amber-100 border border-amber-300">
            <span className="text-[9px] font-medium uppercase tracking-widest text-amber-700">
              {language === 'pt' ? 'Projeto Piloto' : 'Pilot Project'}
            </span>
          </div>
        ) : (
          <div className={`self-start px-2.5 py-1 rounded-full ${colors.light} border ${colors.border}`}>
            <span className={`text-[9px] font-medium uppercase tracking-widest ${colors.text}`}>
              {statusLabels[status]?.[language] || statusLabels[status]?.en || status}
            </span>
          </div>
        )}

        {/* Title section - SERIF for premium identity */}
        <div className="mt-3">
          <h3 className="font-serif text-[24px] font-semibold tracking-tight">
            {typeof title === 'object' ? title[language] : title}
          </h3>
        </div>

        {/* KPIs section - positioned slightly higher */}
        <div className="mt-8">
          {/* KPIs grid */}
          <div className="grid grid-cols-2 gap-2.5">
            {miniKpis.slice(0, 4).map((kpi, idx) => (
              <div 
                key={idx} 
                className={`bg-slate-900/80 rounded-lg px-4 py-2.5 ${kpi.link ? 'hover:bg-slate-900/90 cursor-pointer transition-colors' : ''}`}
                onClick={(e) => kpi.link && handleKpiClick(e, kpi)}
                data-testid={`kpi-${kpi.id || idx}`}
              >
                {/* Label - sans-serif, lighter weight */}
                <div className="text-[9px] text-white/50 uppercase tracking-wider font-normal mb-1">
                  {typeof kpi.label === 'object' ? kpi.label[language] : kpi.label}
                </div>
                {/* Value - slightly smaller */}
                <div className="text-xl font-bold text-white tracking-tight leading-none">{kpi.value}</div>
                {/* Variance vs Budget */}
                {kpi.variance && (
                  <div className={`text-[9px] font-medium mt-1 tracking-wide ${
                    kpi.variance.startsWith('+') ? 'text-emerald-400' : 
                    kpi.variance.startsWith('-') ? 'text-red-400' : 'text-white/50'
                  }`}>
                    {kpi.variance.replace('vs Orç.', '')} {formatVarianceLabel(kpi.variance, 'budget')}
                  </div>
                )}
                {/* Variance YoY (período homólogo) */}
                {kpi.yoy && (
                  <div className={`text-[9px] font-medium tracking-wide ${
                    kpi.yoy.startsWith('+') ? 'text-emerald-400' : 
                    kpi.yoy.startsWith('-') ? 'text-red-400' : 'text-white/50'
                  }`}>
                    {kpi.yoy} {formatVarianceLabel(kpi.yoy, 'yoy')}
                  </div>
                )}
                {!kpi.variance && kpi.trend === 'up' && <TrendingUp className="w-3 h-3 text-emerald-400 mt-1" />}
                {!kpi.variance && kpi.trend === 'down' && <TrendingDown className="w-3 h-3 text-red-400 mt-1" />}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const GlobalDashboard = ({ year, onYearChange }) => {
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const [isTransitioning, setIsTransitioning] = React.useState(false);
  const [displayYear, setDisplayYear] = React.useState(year);
  
  // Handle period change with smooth transition
  const handleYearChange = React.useCallback((newYear) => {
    if (newYear === year) return;
    setIsTransitioning(true);
    setTimeout(() => {
      onYearChange(newYear);
      setDisplayYear(newYear);
      setTimeout(() => {
        setIsTransitioning(false);
      }, 50);
    }, 300);
  }, [year, onYearChange]);

  // Sync displayYear with year prop
  React.useEffect(() => {
    setDisplayYear(year);
  }, [year]);
  
  const areas = mockData.areaSummaries[displayYear];

  const images = {
    operations: "https://customer-assets.emergentagent.com/job_performance-hub-229/artifacts/7eccnei8_015.jpg",
    finance: "https://customer-assets.emergentagent.com/job_performance-hub-229/artifacts/37ldlxie_356.jpg",
    hr: "/images/hr_person_focused.jpg",
    esg: "https://customer-assets.emergentagent.com/job_performance-hub-229/artifacts/lptpz6rk_123.jpg",
  };

  const getOverallStatusLabel = (status, lang) => {
    const labels = {
      above: { pt: 'ACIMA DO PLANO', en: 'ABOVE PLAN' },
      'on-track': { pt: 'NO PLANO', en: 'ON TRACK' },
      below: { pt: 'ABAIXO DO PLANO', en: 'BELOW PLAN' },
    };
    return labels[status]?.[lang] || labels[status]?.en;
  };

  const getOverallStatusColor = (status) => {
    const colors = {
      above: 'text-emerald-600 bg-emerald-50 border-emerald-200',
      'on-track': 'text-blue-600 bg-blue-50 border-blue-200',
      below: 'text-red-600 bg-red-50 border-red-200',
    };
    return colors[status] || colors['on-track'];
  };

  return (
    <div className="max-w-[1600px] mx-auto px-6 pt-1 pb-2" data-testid="global-dashboard">

      {/* Executive KPI Ticker - Dynamic Board-Level Dashboard */}
      <section className="mb-3 -mx-6">
        <ExecutiveKPITicker 
          period={displayYear === 2026 ? '1T 2026' : '2025'} 
          year={displayYear}
          onYearChange={handleYearChange}
        />
      </section>

      {/* Area Cards - with transition animation */}
      <section className="mb-4">
        <div 
          className={`grid grid-cols-4 gap-4 transition-all duration-300 ${
            isTransitioning ? 'opacity-0 transform scale-[0.98]' : 'opacity-100 transform scale-100'
          }`}
        >
          <ActionBlock
            title={{ en: 'Operations', pt: 'Operações' }}
            status={areas.operations.status}
            phrase={areas.operations.phrase}
            miniKpis={areas.operations.miniKpis}
            backgroundImage={images.operations}
            onClick={() => navigate('/operations')}
            delay={100}
            navigate={navigate}
            year={displayYear}
            isPilot={true}
          />
          <ActionBlock
            title={{ en: 'Finance', pt: 'Finanças' }}
            status={areas.finance.status}
            phrase={areas.finance.phrase}
            miniKpis={areas.finance.miniKpis}
            backgroundImage={images.finance}
            onClick={() => navigate('/finance')}
            delay={200}
            navigate={navigate}
            year={displayYear}
            isPilot={true}
          />
          <ActionBlock
            title={{ en: 'Human Resources', pt: 'Recursos Humanos' }}
            status={areas.hr.status}
            phrase={areas.hr.phrase}
            miniKpis={areas.hr.miniKpis}
            backgroundImage={images.hr}
            onClick={() => {}}
            delay={300}
            navigate={navigate}
            year={displayYear}
            inDevelopment={true}
          />
          <ActionBlock
            title={{ en: 'ESG', pt: 'ESG' }}
            status={areas.esg.status}
            phrase={areas.esg.phrase}
            miniKpis={areas.esg.miniKpis}
            backgroundImage={images.esg}
            onClick={() => {}}
            delay={400}
            navigate={navigate}
            year={displayYear}
            inDevelopment={true}
          />
        </div>
      </section>
    </div>
  );
};

export default GlobalDashboard;
